/**
 * Sheet-style "Search" panel — a UNIFIED list of everything.
 *
 * Positions AND moves/counters are always both present, independent of
 * the canvas graph mode; a header pill cycles the kind filter
 * (Positions & Moves → Positions → Moves) and a `kind` column badges each
 * row. Position rows expand into filterable/sortable moves-from /
 * moves-to tables (the same MoveTable the drawer uses) plus counters;
 * move / counter rows expand into their from → to positions and sibling
 * counters.
 *
 * Filtering is faceted: free text matches names, and `type:value` tag
 * chips (family / concept / difficulty / …) combine under an ALL/ANY
 * mode. See src/ui/list-facets.ts.
 *
 * Selection sync: when an external selection lands here (3D click,
 * drawer cross-link, or a drawer's "view in list" button), THAT entity's
 * own row is scrolled into view and auto-expanded — a move expands the
 * move, never the position it starts from.
 *
 * ESC closes the list view.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isSelectionValid, useAppStore } from '../state/store';
import { useResponsivePanelLayout } from './useResponsivePanelLayout';
import { ZONE_COLORS, CLUSTER_COLORS, clusterColorForId } from '../zones/palette';
import { MoveTable } from './MoveTable';
import { ViewSwitcher } from './Drawer';
import { ResizeHandle } from './ResizeHandle';
import {
  buildFacetUniverse,
  makeFacet,
  matchesFacets,
  prettyValue,
  splitFacet,
  type Facet,
  type FacetType,
} from './list-facets';
import type { Counter, Move, Position, Zone } from '../types/domain';
import { normalizeForSearch, searchMatcher } from './search-text';

type SortKey = 'name' | 'kind' | 'positionType' | 'cls' | 'difficulty' | 'meta';

/** Sort order for the kind column: positions, then moves, then counters. */
const KIND_RANK: Record<string, number> = { position: 0, move: 1, counter: 2 };

/**
 * The "class" column — one column that carries the most useful
 * sub-classification for whatever the row IS, since positions and moves
 * have different vocabularies:
 *   - moves / counters → `category` (sweep, pass, submission, escape,
 *     takedown, transition, guard-retention)
 *   - positions        → `posture` (supine, seated, standing, prone, …)
 * Both are 100% populated in the dataset and both already have entries in
 * CLUSTER_COLORS, so the column tints match the map's cluster palette.
 * (Replaced `family`, which was mostly a near-duplicate of the name.)
 */
function classOf(row: {
  rkind: 'position' | 'move' | 'counter';
  category?: string;
  posture?: string;
}): string | undefined {
  return row.rkind === 'position' ? row.posture : row.category;
}

const DIFFICULTY_RANK: Record<string, number> = {
  fundamental: 0,
  intermediate: 1,
  advanced: 2,
  specialist: 3,
};

/** Mirror of Drawer.tsx/cluster.ts. Keep in sync if logic changes. */
function positionTypeForPosition(p: Position): string | undefined {
  if (p.tags?.includes('symmetric')) return 'neutral';
  const z = p.zone;
  if (z === 'submitted') return 'terminal';
  if (z === 'scrambled') return 'flux';
  if (z === 'standing' || z === 'clinch') return 'neutral';
  if (z.endsWith('-top')) return 'top';
  if (z.endsWith('-bottom')) return 'bottom';
  return undefined;
}

function pretty(s: string): string {
  return s
    .split(/[-_]/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

const rowKey = (kind: 'position' | 'move' | 'counter', id: string) => `${kind}:${id}`;

// Column widths are fr-based with minmax floors so the whole row
// shrinks proportionally as the panel narrows — instead of only the
// name column eating the slack (which left the auxiliary columns at
// their fixed widths and the name truncated to "Bac..." even when the
// panel still had space). The chevron column stays a fixed 20px so
// the rotation animation reads consistently regardless of width.
// chevron · kind icon · name · type · class · diff · deg
const COL_TEMPLATE =
  '20px 16px minmax(70px, 3fr) minmax(40px, 0.9fr) minmax(52px, 1.1fr) minmax(46px, 0.9fr) minmax(26px, 0.45fr)';

/** Kind accents — mirror the kind-switch colours so the row icons and
 *  the Positions/Moves control read as the same vocabulary. */
const KIND_BADGE: Record<'position' | 'move' | 'counter', { label: string; color: string }> = {
  position: { label: 'Position', color: '#7aaec0' },
  move: { label: 'Move', color: '#d4a070' },
  counter: { label: 'Counter', color: '#9e7a5c' },
};

/**
 * Row kind glyph. Deliberately echoes the map's own grammar so the list
 * and the canvas teach the same thing: a position is a circle (nodes are
 * circles), a move is an arrow (the line between two circles), and a
 * counter is an arrow intercepted mid-flight.
 */
function KindIcon({ rkind }: { rkind: 'position' | 'move' | 'counter' }) {
  if (rkind === 'position') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
        <circle cx="6" cy="6" r="4.25" fill="currentColor" />
      </svg>
    );
  }
  if (rkind === 'move') {
    return (
      <svg
        width="13"
        height="13"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M1.5 7h9" />
        <path d="M7.5 3.5 11 7l-3.5 3.5" />
      </svg>
    );
  }
  // Counter — an arrow with an interception tick across it.
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1.5 9.5h4l5-5" />
      <path d="M7.5 2.5 11 4.5l-2 3.5" />
    </svg>
  );
}

// ----- Tag-filter chrome -------------------------------------------------

const FACET_NEUTRAL = '#9aa9bd';

/** Colour for a facet chip — families/concepts get their cluster tint,
 *  zones their zone colour, the rest the fixed cluster table or neutral. */
function facetColor(facet: Facet): string {
  const { type, value } = splitFacet(facet);
  if (type === 'family') return clusterColorForId(value, 'family');
  if (type === 'concept') return clusterColorForId(value, 'concept');
  if (type === 'zone') return ZONE_COLORS[value as Zone] ?? FACET_NEUTRAL;
  return CLUSTER_COLORS[value] ?? FACET_NEUTRAL;
}

const dropdownSectionStyle: React.CSSProperties = {
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  opacity: 0.4,
  padding: '4px 8px 2px',
};
const dropdownItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  padding: '6px 8px',
  borderRadius: 4,
  border: 'none',
  background: 'transparent',
  color: '#e6e8ef',
  fontSize: 12,
  cursor: 'pointer',
  textAlign: 'left',
};

/** A selected facet, shown as a removable chip in the filter input. */
function FacetChip({ facet, onRemove }: { facet: Facet; onRemove: () => void }) {
  const { type, value } = splitFacet(facet);
  const color = facetColor(facet);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 3px 1px 7px',
        borderRadius: 8,
        fontSize: 10,
        color,
        background: `${color}1e`,
        border: `1px solid ${color}55`,
      }}
    >
      <span style={{ opacity: 0.6, textTransform: 'uppercase', fontSize: 8, letterSpacing: 0.4 }}>
        {type}
      </span>
      <span>{prettyValue(value)}</span>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={onRemove}
        aria-label="Remove tag"
        style={{
          background: 'none',
          border: 'none',
          color,
          cursor: 'pointer',
          fontSize: 12,
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </span>
  );
}

export function ListView({
  positions,
  moves,
  counters,
}: {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
}) {
  const selection = useAppStore((s) => s.selection);
  const setSelection = useAppStore((s) => s.setSelection);
  const enterDrawerFromList = useAppStore((s) => s.enterDrawerFromList);
  const setPreviewMoveId = useAppStore((s) => s.setPreviewMoveId);
  // Read-side: when the user hovers an edge / sleeve sub-arc in the
  // 3D scene the store's previewMoveId updates. ListView's inner
  // MoveTable rows highlight whichever move matches the focal id,
  // creating the inverse of the row→scene preview link the user
  // already gets when hovering a table row.
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  const drawerWidth = useAppStore((s) => s.drawerWidth);
  // Shared responsive layout: see useResponsivePanelLayout.ts.
  const { asideStyle, innerSurfaceStyle, bottomSheet } =
    useResponsivePanelLayout({
      open: true,
      drawerWidth,
      slideInOnMount: true,
    });

  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ----- Tag filter (facets) — lives in the store so clickable chips
  // anywhere can drive it. `query` above stays local: it's both the
  // autocomplete text and a residual free-text name match.
  const listFilterTags = useAppStore((s) => s.listFilterTags);
  const listFilterMode = useAppStore((s) => s.listFilterMode);
  const addListFilterTag = useAppStore((s) => s.addListFilterTag);
  const removeListFilterTag = useAppStore((s) => s.removeListFilterTag);
  const clearListFilterTags = useAppStore((s) => s.clearListFilterTags);
  const setListFilterMode = useAppStore((s) => s.setListFilterMode);
  const searchFilter = useAppStore((s) => s.searchFilter);
  const setSearchFilter = useAppStore((s) => s.setSearchFilter);
  const setSearchRefSets = useAppStore((s) => s.setSearchRefSets);
  const [autoOpen, setAutoOpen] = useState(false);
  // Unified-list kind filter — cycles both → positions → moves → both.
  const [kindFilter, setKindFilter] = useState<'both' | 'positions' | 'moves'>(
    'both',
  );
  // Each state gets its own accent so the control reads as a real
  // multi-state switch, not a label: neutral for the everything view,
  // blue for positions (bottom/blue in the palette), sand for moves.
  const KIND_META = {
    both: { label: 'Positions & Moves', color: '#b3b6bc' },
    positions: { label: 'Positions', color: '#7aaec0' },
    moves: { label: 'Moves', color: '#d4a070' },
  } as const;
  const cycleKind = () =>
    setKindFilter((k) =>
      k === 'both' ? 'positions' : k === 'positions' ? 'moves' : 'both',
    );
  // Onboarding tour flashes whichever search control it's describing.
  const coachHighlight = useAppStore((s) => s.coachHighlight);
  // The search tour step highlights BOTH the Search tab (see Drawer's
  // ViewSwitcher) and this filter input.
  const flashFilter =
    coachHighlight === 'search'
      ? 'bjjmap-ctrl-flash 2.2s ease-in-out infinite'
      : undefined;
  // expanded set lives in the store so the user's expansion state
  // survives panel close/open and graph-mode swaps. Filter, sort, and
  // scroll position are still local — those reset on remount is fine
  // (filter is typically scoped to the current viewing session).
  const expanded = useAppStore((s) => s.listViewExpanded);
  const toggleListViewExpansion = useAppStore((s) => s.toggleListViewExpansion);
  const expandListViewRow = useAppStore((s) => s.expandListViewRow);

  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Ref to the panel root so the shared ResizeHandle can DOM-mutate
  // its width during a drag (skipping React reconciliation per frame,
  // like the drawer does with its aside + inner refs).
  const panelRef = useRef<HTMLElement>(null);
  // Ref to the inner surface — in side mode it's `position: absolute;
  // right: 0; width: <openWidth>`, so its width is NOT inherited from
  // the aside. During a resize drag we mutate the aside's width
  // directly but the inner stays at its React-rendered width until
  // the next render. That mismatch is what the user was seeing in
  // List / Plan mode on landscape / desktop: the aside grew but the
  // content inside lagged because the inner surface wasn't tracking
  // the drag. The Drawer didn't have this problem because it already
  // mutates BOTH refs in its applyLiveWidth callback.
  const innerRef = useRef<HTMLDivElement>(null);
  // Ref to the scrollable rows container so the auto-scroll-to-
  // selection logic can drive that specific element's scrollTop
  // instead of `scrollIntoView()`, which walks UP the DOM and ends
  // up scrolling the document / the inner surface as well. That
  // bubbling scroll is why switching from Detail / Plan to List in
  // landscape mobile pushed the slider banner above the visible
  // area — the inner surface scrolled, dragging the sticky-looking
  // header row off-screen.
  const rowsScrollRef = useRef<HTMLDivElement>(null);

  const positionsById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const movesById = useMemo(() => new Map(moves.map((m) => [m.id, m])), [moves]);

  // Context-aware Esc. ListView's handler covers the two "list rendered"
  // cases of the four-state ESC matrix (the Drawer covers the other
  // two when it's the rendered panel):
  //   1. List view + selection present → clear the selection (deselect
  //      the canvas node) but KEEP the list panel open with its filter
  //      query, sort, expanded set, and scroll position intact. Lets
  //      the user "drop" a node without losing their browsing context.
  //   2. List view + no selection → close the panel entirely. Nothing
  //      to deselect, so Esc collapses the panel back to its edge-caret
  //      handle.
  // Fresh state read via getState() at event-time to avoid stale-
  // closure surprises when the effect's deps haven't changed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      const state = useAppStore.getState();
      if (state.selection.kind !== 'none') {
        state.setSelection({ kind: 'none' });
      } else {
        state.setListViewOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Per-position degree for the connectivity column / sort axis.
  const degree = useMemo(() => {
    const d = new Map<string, { in: number; out: number }>();
    for (const p of positions) d.set(p.id, { in: 0, out: 0 });
    for (const m of moves) {
      if (d.has(m.from)) d.get(m.from)!.out++;
      if (d.has(m.to)) d.get(m.to)!.in++;
    }
    return d;
  }, [positions, moves]);

  const movesByEndpoint = useMemo(() => {
    const out: Record<string, Move[]> = {};
    const inn: Record<string, Move[]> = {};
    for (const m of moves) {
      (out[m.from] ??= []).push(m);
      (inn[m.to] ??= []).push(m);
    }
    return { from: out, to: inn };
  }, [moves]);

  const countersByMove = useMemo(() => {
    const out: Record<string, Counter[]> = {};
    for (const c of counters) (out[c.intercepts] ??= []).push(c);
    return out;
  }, [counters]);


  // ----- Build rows -----
  // The list is UNIFIED: positions and moves/counters are always both
  // available regardless of the canvas graph mode. `kindFilter` cycles
  // both → positions → moves → both to narrow it.
  type Row =
    | ({ rkind: 'position' } & Position & { _degree: number; _ptype: string | undefined })
    | ({ rkind: 'move' } & Move & { _ptype: string | undefined })
    | ({ rkind: 'counter' } & Counter & { _ptype: string | undefined });

  /**
   * Every entity matching the search text and facets, REGARDLESS of the
   * position/move kind toggle.
   *
   * The kind toggle scopes what you read in the list; it should not
   * decide what the map draws. Publishing the kind-filtered rows meant
   * choosing "Positions" published an empty move set, and the canvas
   * narrower drops any move not in that set — so the edges vanished and
   * the positions floated unconnected. Choosing "Moves" was worse.
   */
  const allMatchingRows: Row[] = useMemo(() => {
    // Free text matches what the user can SEE on a row: its name, its
    // aliases, and its label chips (family / concepts / tags).
    //
    // Deliberately NOT matched: `description` and the slug `id`. Those
    // made results baffling — "guard" returned Side Control because its
    // description says "escape to guard", and "bottom" would hit every
    // entity whose id ends in "-bottom". Structured enums (category, gi,
    // difficulty, zone) are left to the tag autocomplete, which shows you
    // exactly which facet you're filtering on.
    // Folded on both sides, so "north south" finds "North-South" — see
    // search-text.ts. The matcher is built once per query rather than
    // per candidate, because folding the query is the expensive half.
    const matchesQuery = searchMatcher(query);
    /** Name + aliases + label chips. */
    const matchesLabels = (e: {
      name: string;
      aliases?: string[];
      family?: string;
      concepts?: string[];
      tags?: string[];
    }) =>
      matchesQuery(e.name) ||
      (e.aliases ?? []).some(matchesQuery) ||
      matchesQuery(e.family) ||
      (e.concepts ?? []).some(matchesQuery) ||
      (e.tags ?? []).some(matchesQuery);

    const rows: Row[] = [];

    {
      for (const p of positions) {
        if (!matchesFacets(p, listFilterTags, listFilterMode)) continue;
        if (matchesLabels(p)) {
          const d = degree.get(p.id);
          const totalDeg = (d?.in ?? 0) + (d?.out ?? 0);
          rows.push({
            rkind: 'position',
            ...p,
            _degree: totalDeg,
            _ptype: positionTypeForPosition(p),
          });
        }
      }
    }
    {
      for (const m of moves) {
        if (!matchesFacets(m, listFilterTags, listFilterMode)) continue;
        if (matchesLabels(m)) {
          const fromPos = positionsById.get(m.from);
          rows.push({
            rkind: 'move',
            ...m,
            _ptype: fromPos ? positionTypeForPosition(fromPos) : undefined,
          });
        }
      }
      for (const c of counters) {
        if (!matchesFacets(c, listFilterTags, listFilterMode)) continue;
        if (matchesLabels(c)) {
          const fromPos = positionsById.get(c.from);
          rows.push({
            rkind: 'counter',
            ...c,
            _ptype: fromPos ? positionTypeForPosition(fromPos) : undefined,
          });
        }
      }
    }

    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'kind') {
        cmp = (KIND_RANK[a.rkind] ?? 9) - (KIND_RANK[b.rkind] ?? 9);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'positionType') {
        cmp = (a._ptype ?? '~').localeCompare(b._ptype ?? '~');
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'cls') {
        cmp = (classOf(a) ?? '~').localeCompare(classOf(b) ?? '~');
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'difficulty') {
        cmp =
          (DIFFICULTY_RANK[a.difficulty ?? ''] ?? 99) -
          (DIFFICULTY_RANK[b.difficulty ?? ''] ?? 99);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'meta') {
        // Unified degree across all kinds. Positions = in+out moves
        // incident on the position. Moves = in-chains + out-chains
        // (moves whose to/from match this move's from/to). Counters use
        // the same shape as moves with `result` standing in for `to`.
        const degOf = (r: Row): number => {
          if (r.rkind === 'position') return r._degree;
          if (r.rkind === 'move')
            return (
              (movesByEndpoint.from[r.to]?.length ?? 0) +
              (movesByEndpoint.to[r.from]?.length ?? 0)
            );
          return (
            (movesByEndpoint.from[r.result]?.length ?? 0) +
            (movesByEndpoint.to[r.from]?.length ?? 0)
          );
        };
        cmp = degOf(a) - degOf(b);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [
    positions,
    moves,
    counters,
    query,
    sortKey,
    sortDir,
    degree,
    positionsById,
    listFilterTags,
    listFilterMode,
  ]);

  /**
   * What the LIST shows: the matches, narrowed by the kind toggle.
   * The canvas deliberately reads allMatchingRows instead, so toggling
   * between positions and moves changes what you are reading without
   * tearing the graph apart underneath it.
   */
  const filteredRows: Row[] = useMemo(
    () =>
      allMatchingRows.filter((r) =>
        kindFilter === 'positions'
          ? r.rkind === 'position'
          : kindFilter === 'moves'
            ? r.rkind !== 'position'
            : true,
      ),
    [allMatchingRows, kindFilter],
  );


  // ----- Selection sync -----
  // When selection changes, scroll to the matching row + auto-expand.
  //
  // The list is UNIFIED, so every selection has its OWN row — a move
  // expands the move, not the position it happens to start from. (The
  // old mode-scoped code redirected move/counter selections to their
  // from-position row, which in the merged list read as "clicked a move,
  // got scrolled to some position instead".) If the row genuinely isn't
  // rendered — the kind filter is narrowed, or a tag/text filter excludes
  // it — we simply do nothing rather than jumping somewhere unrelated.
  useLayoutEffect(() => {
    if (selection.kind === 'none') return;

    const targetKey =
      selection.kind === 'position'
        ? rowKey('position', selection.id)
        : selection.kind === 'move'
          ? rowKey('move', selection.id)
          : rowKey('counter', selection.id);

    // Only act if that row actually exists in the current result set.
    if (!filteredRows.some((r) => rowKey(r.rkind, r.id) === targetKey)) return;
    const key = targetKey;

    expandListViewRow(key);

    const raf = requestAnimationFrame(() => {
      const el = rowRefs.current.get(key);
      const scroller = rowsScrollRef.current;
      if (!el || !scroller) return;
      // Manual scrollTop calc instead of el.scrollIntoView() — the
      // built-in walks UP the DOM finding every scrollable ancestor
      // and scrolls each one to bring `el` into view, which on
      // landscape mobile was also scrolling the inner surface (a
      // bottom-sheet sibling of the page body) and pushing the
      // slider banner above the visible area. Driving scrollTop on
      // the rows container directly guarantees ONLY that container
      // scrolls; the panel's sticky-looking header stays where it
      // sat. PADDING = scroll-padding-top so the row's header line
      // doesn't hug the very top edge against the column-header row.
      const PADDING = 12;
      const elTopInScroller =
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      const target = Math.max(0, elTopInScroller - PADDING);
      scroller.scrollTo({ top: target, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [selection, filteredRows, expandListViewRow]);

  // Facet universe for the autocomplete — only tags present in the current
  // list mode's entities, so we never suggest a tag that matches nothing.
  const facetUniverse = useMemo(
    () =>
      buildFacetUniverse([
        ...(kindFilter !== 'moves' ? positions : []),
        ...(kindFilter !== 'positions' ? [...moves, ...counters] : []),
      ]),
    [kindFilter, positions, moves, counters],
  );

  // Autocomplete suggestions from the current query: matching tags (not
  // already selected) then matching node names.
  const suggestions = useMemo(() => {
    // Emptiness is tested on the FOLDED query: a box holding only "-"
    // has nothing to suggest from, and suggesting on a query that
    // matches everything would just dump the first 8 tags.
    const folded = normalizeForSearch(query);
    const match = searchMatcher(query);
    if (folded.length === 0) return { tags: [] as { facet: Facet; count: number }[], nodes: [] as { kind: 'position' | 'move' | 'counter'; id: string; name: string; moveId?: string }[] };
    const tags = [...facetUniverse.entries()]
      .filter(([facet]) => {
        if (listFilterTags.includes(facet)) return false;
        const { type, value } = splitFacet(facet);
        return match(value) || match(type);
      })
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([facet, count]) => ({ facet, count }));
    const nodeSrc = [
      ...(kindFilter !== 'moves'
        ? positions.map((p) => ({
            kind: 'position' as const,
            id: p.id,
            name: p.name,
          }))
        : []),
      ...(kindFilter !== 'positions'
        ? [
            ...moves.map((m) => ({ kind: 'move' as const, id: m.id, name: m.name })),
            ...counters.map((c) => ({
              kind: 'counter' as const,
              id: c.id,
              name: c.name,
              moveId: c.intercepts,
            })),
          ]
        : []),
    ];
    const nodes = nodeSrc
      .filter((n) => match(n.name))
      .slice(0, 6);
    return { tags, nodes };
  }, [query, facetUniverse, listFilterTags, kindFilter, positions, moves, counters]);

  const commitTag = (facet: Facet) => {
    addListFilterTag(facet);
    setQuery('');
    setAutoOpen(false);
  };
  const jumpToNode = (n: {
    kind: 'position' | 'move' | 'counter';
    id: string;
    moveId?: string;
  }) => {
    if (n.kind === 'counter') {
      setSelection({ kind: 'counter', id: n.id, moveId: n.moveId ?? '' });
    } else {
      setSelection({ kind: n.kind, id: n.id });
    }
    setQuery('');
    setAutoOpen(false);
  };

  const headerSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Thin alias to the store action so existing call sites keep their
  // local-looking shape (they pass a key, the store handles the rest).
  const toggleExpand = (key: string) => toggleListViewExpansion(key);

  const onRowClick = useCallback(
    (rk: 'position' | 'move' | 'counter', id: string, intercepts?: string) => {
      if (rk === 'counter') {
        setSelection({ kind: 'counter', id, moveId: intercepts ?? '' });
      } else {
        setSelection({ kind: rk, id });
      }
    },
    [setSelection],
  );

  /**
   * Chevron click handler. Asymmetric:
   *  - Row is currently collapsed → treat as a selection (zooms the
   *    camera, highlights the node, and the selection-sync layout
   *    effect auto-expands the matching row).
   *  - Row is currently expanded → just collapse it locally. Selection
   *    stays where it was; the user might be browsing the list while
   *    keeping a separate node selected.
   *
   * This makes "open the chevron on a row" feel equivalent to clicking
   * the row body — both ways now register as selecting the entity.
   */
  const onChevron = useCallback(
    (
      key: string,
      rk: 'position' | 'move' | 'counter',
      id: string,
      intercepts?: string,
    ) => {
      if (expanded.has(key)) {
        toggleExpand(key);
      } else {
        onRowClick(rk, id, intercepts);
      }
    },
    [expanded, onRowClick],
  );

  const onViewDetails = useCallback(
    (rk: 'position' | 'move' | 'counter', id: string, intercepts?: string) => {
      if (rk === 'counter') {
        enterDrawerFromList({ kind: 'counter', id, moveId: intercepts ?? '' });
      } else {
        enterDrawerFromList({ kind: rk, id });
      }
    },
    [enterDrawerFromList],
  );

  // Move-table cross-link handlers that route through enterDrawerFromList
  // so back-nav restores list view. These are passed into the inner
  // MoveTable instances inside position expansions.
  const innerMoveDetails = useCallback(
    (id: string) => enterDrawerFromList({ kind: 'move', id }),
    [enterDrawerFromList],
  );

  // Total for the CURRENT kind filter (the list is unified, so this is
  // no longer tied to the canvas graph mode).
  const totalCount =
    (kindFilter !== 'moves' ? positions.length : 0) +
    (kindFilter !== 'positions' ? moves.length + counters.length : 0);

  // Canvas eye: publish the current result ids so App can narrow the 3D
  // map to exactly what the list is showing. Only while the eye is on —
  // otherwise every keystroke would re-run the force sim for nothing.
  //
  // CRITICAL: publish only when the id CONTENT changes, never on identity.
  // This effect sits in a feedback loop — publishing re-runs App's filter,
  // which hands ListView new arrays, which rebuilds `allMatchingRows` with a
  // fresh identity, which would re-fire this effect. Emitting new Set
  // objects every time made that loop infinite (React error #185,
  // "maximum update depth exceeded"). The signature check breaks it: once
  // the results settle, the content matches and we stop publishing.
  const lastPublishedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!searchFilter) {
      if (lastPublishedRef.current !== null) {
        lastPublishedRef.current = null;
        setSearchRefSets(null);
      }
      return;
    }
    const posIds = new Set<string>();
    const moveIds = new Set<string>();
    const counterIds = new Set<string>();
    for (const r of allMatchingRows) {
      if (r.rkind === 'position') posIds.add(r.id);
      else if (r.rkind === 'move') moveIds.add(r.id);
      else counterIds.add(r.id);
    }
    // Sorted so a pure re-sort of the list doesn't count as a change.
    const sig =
      [...posIds].sort().join(',') +
      '|' +
      [...moveIds].sort().join(',') +
      '|' +
      [...counterIds].sort().join(',');
    if (sig === lastPublishedRef.current) return;
    lastPublishedRef.current = sig;
    setSearchRefSets({ posIds, moveIds, counterIds });
  }, [searchFilter, allMatchingRows, setSearchRefSets]);

  // Dropping the panel shouldn't leave the canvas filtered to a list the
  // user can no longer see.
  useEffect(
    () => () => {
      lastPublishedRef.current = null;
      setSearchRefSets(null);
    },
    [setSearchRefSets],
  );

  const selectedRowKey =
    selection.kind === 'position'
      ? rowKey('position', selection.id)
      : selection.kind === 'move'
        ? rowKey('move', selection.id)
        : selection.kind === 'counter'
          ? rowKey('counter', selection.id)
          : null;

  return (
    <>
    <aside
      ref={panelRef}
      style={{ ...asideStyle, color: '#e6e8ef' }}
    >
    <div
      ref={innerRef}
      style={{
        ...innerSurfaceStyle,
        background: '#0e1018',
        display: 'flex',
        flexDirection: 'column',
        ...(bottomSheet
          ? { borderTop: '1px solid rgba(255,255,255,0.08)' }
          : { borderLeft: '1px solid rgba(255,255,255,0.08)' }),
      }}
    >
      {/* Header — Detail/List switcher on its own row, then label +
          close, then filter. The switcher's "detail" tab closes list
          view (drawer takes over with the current selection). */}
      <div
        style={{
          padding: '10px 16px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <ViewSwitcher
          activeView="list"
          hasSelection={isSelectionValid(selection, positions, moves, counters)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Kind cycle: Positions & Moves → Positions → Moves → …
              Sits LEFT of the count. Styled as an obvious control — its
              own accent per state, plus a ⇄ affordance glyph. */}
          <button
            data-tour="search-kind"
            onClick={cycleKind}
            title={`Showing ${KIND_META[kindFilter].label} — click to change`}
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 10px',
              borderRadius: 999,
              border: `1px solid ${KIND_META[kindFilter].color}88`,
              background: `${KIND_META[kindFilter].color}22`,
              color: KIND_META[kindFilter].color,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.3,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 120ms ease, border-color 120ms ease',
            }}
          >
            <span aria-hidden style={{ opacity: 0.8, fontSize: 11 }}>
              ⇄
            </span>
            {KIND_META[kindFilter].label}
          </button>
          <span
            style={{
              fontSize: 11,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              opacity: 0.55,
              flex: 1,
              minWidth: 0,
              textAlign: 'right',
            }}
          >
            {filteredRows.length} of {totalCount}
          </span>
          {/* Canvas eye — mirrors the gameplan panel's filter-to-plan
              eyeball: on = the 3D map shows only these results. */}
          <button
            onClick={() => setSearchFilter(!searchFilter)}
            aria-pressed={searchFilter}
            aria-label={
              searchFilter
                ? 'Showing only these results on the map'
                : 'Filter the map to these results'
            }
            title={
              searchFilter
                ? 'Showing only these results — click to show the full map'
                : 'Filter the map to these results'
            }
            style={{
              flexShrink: 0,
              width: 30,
              height: 24,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              border: searchFilter
                ? '1px solid rgba(155,231,255,0.55)'
                : '1px solid rgba(255,255,255,0.10)',
              background: searchFilter
                ? 'rgba(155,231,255,0.14)'
                : 'transparent',
              color: searchFilter ? '#cdf6ff' : '#9aa3af',
              cursor: 'pointer',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
          {/* Token input: selected tag chips + free text, with an
              autocomplete dropdown of matching tags and node names. */}
          <div
            data-tour="search-filter"
            style={{ position: 'relative', flex: 1, minWidth: 0 }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 4,
                padding: '4px 6px',
                minHeight: 30,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4,
                animation: flashFilter,
              }}
            >
              {listFilterTags.map((f) => (
                <FacetChip key={f} facet={f} onRemove={() => removeListFilterTag(f)} />
              ))}
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setAutoOpen(true);
                }}
                onFocus={() => setAutoOpen(true)}
                onBlur={() => window.setTimeout(() => setAutoOpen(false), 120)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setAutoOpen(false);
                  } else if (e.key === 'Enter') {
                    // Enter does NOT adopt the top suggestion. It used to
                    // commit suggestions.tags[0] as a chip, which threw
                    // away what you actually typed and filtered by a tag
                    // you never chose — typing "guard" and pressing enter
                    // would swap your text for whichever facet happened to
                    // rank first. Free text is a legitimate query on its
                    // own, so Enter just dismisses the dropdown and leaves
                    // the query in force. Tags and nodes apply only on an
                    // explicit click/tap.
                    e.preventDefault();
                    setAutoOpen(false);
                  } else if (
                    e.key === 'Backspace' &&
                    query === '' &&
                    listFilterTags.length > 0
                  ) {
                    removeListFilterTag(listFilterTags[listFilterTags.length - 1]);
                  }
                }}
                placeholder={
                  listFilterTags.length > 0 ? 'add tag…' : 'filter by name or tag…'
                }
                style={{
                  flex: 1,
                  minWidth: 90,
                  background: 'transparent',
                  border: 'none',
                  color: '#e6e8ef',
                  fontSize: 12,
                  outline: 'none',
                  padding: '2px',
                }}
              />
              {(listFilterTags.length > 0 || query) && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    clearListFilterTags();
                    setQuery('');
                  }}
                  title="Clear filter"
                  aria-label="Clear filter"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#9aa9bd',
                    cursor: 'pointer',
                    fontSize: 13,
                    lineHeight: 1,
                    padding: '0 2px',
                  }}
                >
                  ×
                </button>
              )}
            </div>
            {autoOpen &&
              (suggestions.tags.length > 0 || suggestions.nodes.length > 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    maxHeight: 280,
                    overflowY: 'auto',
                    background: 'rgba(16,18,26,0.98)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 6,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    padding: 4,
                  }}
                >
                  {suggestions.tags.length > 0 && (
                    <div style={dropdownSectionStyle}>tags</div>
                  )}
                  {suggestions.tags.map(({ facet, count }) => {
                    const { type, value } = splitFacet(facet);
                    const color = facetColor(facet);
                    return (
                      <button
                        key={facet}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitTag(facet)}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.background =
                            'rgba(255,255,255,0.06)')
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.background =
                            'transparent')
                        }
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 2,
                            background: color,
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ opacity: 0.5, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {type}
                        </span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {prettyValue(value)}
                        </span>
                        <span style={{ opacity: 0.4, fontSize: 10 }}>{count}</span>
                      </button>
                    );
                  })}
                  {suggestions.nodes.length > 0 && (
                    <div style={dropdownSectionStyle}>nodes</div>
                  )}
                  {suggestions.nodes.map((n) => (
                    <button
                      key={`${n.kind}:${n.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => jumpToNode(n)}
                      style={dropdownItemStyle}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLButtonElement).style.background =
                          'rgba(255,255,255,0.06)')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLButtonElement).style.background =
                          'transparent')
                      }
                    >
                      <span style={{ opacity: 0.5, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {n.kind}
                      </span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {n.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
          </div>
          {/* AND / OR mode toggle — inline, right of the filter. */}
          <button
            onClick={() =>
              setListFilterMode(listFilterMode === 'all' ? 'any' : 'all')
            }
            title={
              listFilterMode === 'all'
                ? 'Matching tags: ALL (AND) — click for ANY'
                : 'Matching tags: ANY (OR) — click for ALL'
            }
            style={{
              flexShrink: 0,
              alignSelf: 'stretch',
              padding: '0 12px',
              borderRadius: 4,
              border: '1px solid rgba(155,231,255,0.35)',
              background: 'rgba(155,231,255,0.12)',
              color: '#cdf6ff',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              cursor: 'pointer',
            }}
          >
            {listFilterMode === 'all' ? 'ALL' : 'ANY'}
          </button>
        </div>
      </div>

      {/* Column header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COL_TEMPLATE,
          gap: 6,
          padding: '6px 12px',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          opacity: 0.55,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <span></span>
        {/* Kind-icon column — sortable, but the glyphs speak for
            themselves so the header stays unlabelled. */}
        <span
          onClick={() => headerSort('kind')}
          title="Sort by kind"
          style={{ cursor: 'pointer', textAlign: 'center' }}
        >
          {sortKey === 'kind' ? (sortDir === 'asc' ? '↑' : '↓') : '·'}
        </span>
        <span onClick={() => headerSort('name')} style={{ cursor: 'pointer' }}>
          name{sortArrow('name')}
        </span>
        <span onClick={() => headerSort('positionType')} style={{ cursor: 'pointer' }}>
          type{sortArrow('positionType')}
        </span>
        <span onClick={() => headerSort('cls')} style={{ cursor: 'pointer' }}>
          class{sortArrow('cls')}
        </span>
        <span onClick={() => headerSort('difficulty')} style={{ cursor: 'pointer' }}>
          diff{sortArrow('difficulty')}
        </span>
        <span
          onClick={() => headerSort('meta')}
          style={{ cursor: 'pointer', textAlign: 'right' }}
        >
          deg{sortArrow('meta')}
        </span>
      </div>

      {/* Rows */}
      <div
        ref={rowsScrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          // scrollPaddingTop is now a backup — the auto-scroll logic
          // drives scrollTop directly so a row's header lands the
          // right distance below the column-header row regardless of
          // browser quirks with scrollIntoView's chained ancestor
          // scrolling.
          scrollPaddingTop: 12,
        }}
      >
        {filteredRows.map((row) => {
          const key = rowKey(row.rkind, row.id);
          const isExpanded = expanded.has(key);
          const isSelected = selectedRowKey === key;

          if (row.rkind === 'position') {
            const movesFrom = movesByEndpoint.from[row.id] ?? [];
            const movesTo = movesByEndpoint.to[row.id] ?? [];
            return (
              <div
                key={key}
                ref={(el) => {
                  if (el) rowRefs.current.set(key, el);
                  else rowRefs.current.delete(key);
                }}
              >
                <RowHeader
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  onClickRow={() => onRowClick('position', row.id)}
                  onClickChevron={() => onChevron(key, 'position', row.id)}
                  rkind="position"
                  label={row.name}
                  positionType={row._ptype}
                  cls={classOf(row)}
                  difficulty={row.difficulty}
                  meta={String(row._degree)}
                />
                {isExpanded && (
                  <ExpandPanel>
                    <ChipRack>
                      {row._ptype && <Chip kind="positionType" label={row._ptype} />}
                      {row.zone && <Chip kind="zone" label={row.zone} />}
                      {row.dominance && <Chip kind="dominance" label={row.dominance} />}
                      {row.posture && <Chip kind="posture" label={row.posture} />}
                      {row.family && <Chip kind="family" label={row.family} />}
                      {row.isCanonical && row.family && (
                        <Chip kind="canonical" label="canonical" />
                      )}
                      {row.difficulty && <Chip kind="difficulty" label={row.difficulty} />}
                      {(row.concepts ?? []).map((c) => (
                        <Chip key={c} kind="concept" label={c} />
                      ))}
                      {(row.tags ?? []).map((t) => (
                        <Chip key={t} kind="tag" label={t} />
                      ))}
                    </ChipRack>
                    {row.aliases && row.aliases.length > 0 && (
                      <SmallLine>aliases: {row.aliases.join(', ')}</SmallLine>
                    )}
                    {row.description && (
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                        {row.description}
                      </div>
                    )}
                    <ViewDetailsLink onClick={() => onViewDetails('position', row.id)} />
                    <MoveTable
                      title="Moves From Here"
                      direction="→"
                      moves={movesFrom}
                      positions={positions}
                      onSelect={(id) => onRowClick('move', id)}
                      onPreview={setPreviewMoveId}
                      onViewDetails={innerMoveDetails}
                      highlightedId={
                        // Preview (3D edge hover) wins over selection
                        // so a sub-arc hover lights up the matching
                        // row even while a different move is selected.
                        previewMoveId ??
                        (selection.kind === 'move' ? selection.id : null)
                      }
                    />
                    <MoveTable
                      title="Moves To Here"
                      direction="←"
                      moves={movesTo}
                      positions={positions}
                      onSelect={(id) => onRowClick('move', id)}
                      onPreview={setPreviewMoveId}
                      onViewDetails={innerMoveDetails}
                      highlightedId={
                        previewMoveId ??
                        (selection.kind === 'move' ? selection.id : null)
                      }
                    />
                  </ExpandPanel>
                )}
              </div>
            );
          }

          if (row.rkind === 'move') {
            const fromPos = positionsById.get(row.from);
            const toPos = positionsById.get(row.to);
            const moveCounters = countersByMove[row.id] ?? [];
            return (
              <div
                key={key}
                ref={(el) => {
                  if (el) rowRefs.current.set(key, el);
                  else rowRefs.current.delete(key);
                }}
              >
                <RowHeader
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  onClickRow={() => onRowClick('move', row.id)}
                  onClickChevron={() => onChevron(key, 'move', row.id)}
                  rkind="move"
                  label={row.name}
                  positionType={row._ptype}
                  cls={classOf(row)}
                  difficulty={row.difficulty}
                  meta={String(
                    (movesByEndpoint.from[row.to]?.length ?? 0) +
                      (movesByEndpoint.to[row.from]?.length ?? 0),
                  )}
                  // Fall back to the raw id rather than hiding the row's
                  // route: an id is ugly but it is still an answer, and a
                  // silently missing endpoint would look like the move
                  // goes nowhere.
                  route={{
                    from: fromPos?.name ?? row.from,
                    to: toPos?.name ?? row.to,
                  }}
                />
                {isExpanded && (
                  <ExpandPanel>
                    <ChipRack>
                      {row._ptype && <Chip kind="positionType" label={row._ptype} />}
                      <Chip kind="category" label={row.category} />
                      {row.submissionType && (
                        <Chip kind="submissionType" label={row.submissionType} />
                      )}
                      {row.gi && <Chip kind="gi" label={row.gi} />}
                      {row.family && <Chip kind="family" label={row.family} />}
                      {row.isCanonical && row.family && (
                        <Chip kind="canonical" label="canonical" />
                      )}
                      {row.difficulty && <Chip kind="difficulty" label={row.difficulty} />}
                      {(row.concepts ?? []).map((c) => (
                        <Chip key={c} kind="concept" label={c} />
                      ))}
                      {(row.tags ?? []).map((t) => (
                        <Chip key={t} kind="tag" label={t} />
                      ))}
                    </ChipRack>
                    <PositionPair
                      from={fromPos}
                      to={toPos}
                      fromId={row.from}
                      toId={row.to}
                      onSelect={(id) =>
                        enterDrawerFromList({ kind: 'position', id })
                      }
                    />
                    {row.aliases && row.aliases.length > 0 && (
                      <SmallLine>aliases: {row.aliases.join(', ')}</SmallLine>
                    )}
                    {row.description && (
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                        {row.description}
                      </div>
                    )}
                    <ViewDetailsLink onClick={() => onViewDetails('move', row.id)} />
                    {moveCounters.length > 0 && (
                      <InnerCounterList
                        title="Counters / Interceptions"
                        counters={moveCounters}
                        positionsById={positionsById}
                        movesById={movesById}
                        onSelect={(id, intercepts) =>
                          onRowClick('counter', id, intercepts)
                        }
                        onViewDetails={(id, intercepts) =>
                          onViewDetails('counter', id, intercepts)
                        }
                      />
                    )}
                  </ExpandPanel>
                )}
              </div>
            );
          }

          // Counter row.
          const interceptedMove = movesById.get(row.intercepts);
          const resultPos = positionsById.get(row.result);
          const fromPos = positionsById.get(row.from);
          return (
            <div
              key={key}
              ref={(el) => {
                if (el) rowRefs.current.set(key, el);
                else rowRefs.current.delete(key);
              }}
            >
              <RowHeader
                isExpanded={isExpanded}
                isSelected={isSelected}
                onClickRow={() => onRowClick('counter', row.id, row.intercepts)}
                onClickChevron={() => onChevron(key, 'counter', row.id, row.intercepts)}
                  rkind="counter"
                label={row.name}
                positionType={row._ptype}
                cls={classOf(row)}
                difficulty={row.difficulty}
                meta={String(
                  (movesByEndpoint.from[row.result]?.length ?? 0) +
                    (movesByEndpoint.to[row.from]?.length ?? 0),
                )}
                // A counter's destination is `result` — where intercepting
                // the move leaves you — not the move's own `to`, which is
                // where it would have gone had the counter not landed.
                route={{
                  from: fromPos?.name ?? row.from,
                  to: resultPos?.name ?? row.result,
                }}
              />
              {isExpanded && (
                <ExpandPanel>
                  <ChipRack>
                    {row._ptype && <Chip kind="positionType" label={row._ptype} />}
                    <Chip kind="category" label={row.category} />
                    {row.gi && <Chip kind="gi" label={row.gi} />}
                    {row.family && <Chip kind="family" label={row.family} />}
                    {row.isCanonical && row.family && (
                      <Chip kind="canonical" label="canonical" />
                    )}
                    {row.difficulty && <Chip kind="difficulty" label={row.difficulty} />}
                    {typeof row.probability === 'number' && (
                      <Chip
                        kind="probability"
                        label={`${(row.probability * 100).toFixed(0)}% lands`}
                      />
                    )}
                    {(row.concepts ?? []).map((c) => (
                      <Chip key={c} kind="concept" label={c} />
                    ))}
                    {(row.tags ?? []).map((t) => (
                      <Chip key={t} kind="tag" label={t} />
                    ))}
                  </ChipRack>
                  <PositionPair
                    from={fromPos}
                    to={resultPos}
                    fromId={row.from}
                    toId={row.result}
                    onSelect={(id) =>
                      enterDrawerFromList({ kind: 'position', id })
                    }
                  />
                  {row.aliases && row.aliases.length > 0 && (
                    <SmallLine>aliases: {row.aliases.join(', ')}</SmallLine>
                  )}
                  {row.description && (
                    <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                      {row.description}
                    </div>
                  )}
                  {interceptedMove && (
                    <SmallLine>
                      intercepts:{' '}
                      <Link
                        onClick={() =>
                          enterDrawerFromList({ kind: 'move', id: interceptedMove.id })
                        }
                      >
                        {interceptedMove.name}
                      </Link>
                    </SmallLine>
                  )}
                  <ViewDetailsLink
                    onClick={() =>
                      onViewDetails('counter', row.id, row.intercepts)
                    }
                  />
                </ExpandPanel>
              )}
            </div>
          );
        })}
        {filteredRows.length === 0 && (
          <div style={{ padding: 24, opacity: 0.45, fontSize: 12 }}>(no matches)</div>
        )}
      </div>
    </div>
    </aside>
    {/* Side-mode resize handle. Mutates the panel root's width directly
        during a drag so React doesn't re-render every pointer frame.
        In bottom-sheet mode the horizontal handle is meaningless. */}
    {/* ResizeHandle is now mode-aware (horizontal bar on the side
        drawer, vertical bar at the top of the bottom sheet) so it
        ALWAYS renders. The bottomSheet flag was previously used to
        suppress the side-only handle on mobile portrait. */}
    <ResizeHandle
      open
      applyLiveWidth={(size, axis = 'width') => {
        const cssV = `${size}px`;
        if (panelRef.current) {
          // Horizontal mode mutates width (side drawer); vertical
          // mode (bottom sheet) mutates height. The flexbox parent
          // sees the change immediately and the canvas reflows.
          panelRef.current.style[axis] = cssV;
        }
        if (innerRef.current && axis === 'width') {
          // Side mode: inner is `position: absolute; right: 0;
          // width: <openWidth>`, so its width DOESN'T inherit
          // from the aside. Mutate it in lockstep so the content
          // tracks the drag instead of lagging behind. Bottom-
          // sheet mode uses `top:0; bottom:0` on the inner so it
          // auto-fills the resized aside — no manual mutation.
          innerRef.current.style.width = cssV;
        }
      }}
    />
    </>
  );
}

// ----- Sub-components ---------------------------------------------------

/**
 * "Mount (top) → Spider Web (top)" under a move's name.
 *
 * A move's name says what you DO; on its own it does not say where you
 * are standing when you do it or where you end up, which for a graph
 * whose entire subject is how positions connect is the one thing a row
 * should not make you expand to find out. Armbar from where?
 *
 * ON ITS OWN LINE, not appended to the name. This panel is resizable and
 * gets narrow, and inline the route would be the first thing the
 * ellipsis ate — leaving exactly the rows that need it most showing
 * nothing. A second line costs vertical space, which the list has, and
 * makes move rows taller than position rows, which is honest: they carry
 * more information.
 *
 * EACH ENDPOINT TRUNCATES SEPARATELY. Both are flex children with
 * min-width 0 and their own ellipsis, so "Knee Shield Half Guard →
 * Mount" clips the long name and keeps the short one whole. Truncating
 * the line as a single string would let one long endpoint swallow the
 * arrow and the other endpoint together, which reads as a missing
 * destination rather than a clipped one. The arrow never shrinks.
 *
 * SELF-LOOPS GET THEIR OWN FORM. 25 moves start and end in the same
 * position — a grip change, a failed submission you keep — and printing
 * "Saddle (Inside Sankaku) (bottom) → Saddle (Inside Sankaku) (bottom)"
 * states it accurately while looking exactly like a bug. Worse, the
 * longest routes on the map are these, so it is also the case most
 * likely to be clipped into "Saddle (Inside Sankaku)… → Saddle (Insid…".
 * A loop glyph and the position once says the same thing and reads as
 * deliberate. These are hidden on the canvas, so the list is the only
 * place they appear at all.
 */
function RouteLine({ from, to }: { from: string; to: string }) {
  const cell: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  if (from === to) {
    return (
      <span
        title={`Starts and ends in ${from}`}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
          marginTop: 1,
          fontSize: 10.5,
          opacity: 0.62,
          letterSpacing: 0.1,
        }}
      >
        <span style={{ flex: 'none', opacity: 0.7 }}>↻</span>
        <span style={cell}>{from}</span>
      </span>
    );
  }
  return (
    <span
      // The full route on hover, since either end may be clipped.
      title={`${from} → ${to}`}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 4,
        marginTop: 1,
        fontSize: 10.5,
        opacity: 0.62,
        letterSpacing: 0.1,
      }}
    >
      <span style={cell}>{from}</span>
      <span style={{ flex: 'none', opacity: 0.7 }}>→</span>
      <span style={cell}>{to}</span>
    </span>
  );
}

function RowHeader({
  isExpanded,
  isSelected,
  onClickRow,
  onClickChevron,
  label,
  rkind,
  positionType,
  cls,
  difficulty,
  meta,
  route,
}: {
  isExpanded: boolean;
  isSelected: boolean;
  onClickRow: () => void;
  onClickChevron: () => void;
  label: string;
  rkind: 'position' | 'move' | 'counter';
  positionType?: string;
  cls?: string;
  difficulty?: string;
  meta: string;
  /** Where a move or counter starts and ends. Positions have no route. */
  route?: { from: string; to: string };
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        gap: 6,
        padding: '8px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        fontSize: 12,
        alignItems: 'center',
        cursor: 'pointer',
        background: isSelected ? 'rgba(155,231,255,0.10)' : 'transparent',
      }}
      onClick={onClickRow}
      onMouseEnter={(e) => {
        if (!isSelected)
          (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.025)';
      }}
      onMouseLeave={(e) => {
        if (!isSelected)
          (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    >
      <span
        onClick={(e) => {
          e.stopPropagation();
          onClickChevron();
        }}
        style={{
          cursor: 'pointer',
          // Fixed-size box so the glyph swap below doesn't cause any
          // horizontal drift. Rotating "▸" introduced a visible shift
          // because the rotated glyph's geometric center differs from
          // its baseline-anchored position — swapping for the dedicated
          // down-triangle glyph keeps the indicator in place.
          display: 'inline-block',
          width: 12,
          textAlign: 'center',
          opacity: 0.7,
        }}
      >
        {isExpanded ? '▾' : '▸'}
      </span>
      {/* Kind icon — circle = position (nodes are circles), arrow =
          move (the lines between them), forked arrow = counter. */}
      <span
        title={KIND_BADGE[rkind].label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: KIND_BADGE[rkind].color,
        }}
      >
        <KindIcon rkind={rkind} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        {route && <RouteLine from={route.from} to={route.to} />}
      </span>
      <span style={{ opacity: 0.7, fontSize: 11, minWidth: 0 }}>
        {positionType ? (
          <span
            style={{
              color: CLUSTER_COLORS[positionType] ?? '#cad3df',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              fontSize: 10,
            }}
          >
            {positionType}
          </span>
        ) : (
          <span style={{ opacity: 0.3 }}>—</span>
        )}
      </span>
      <span
        style={{
          fontSize: 10.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: cls ? CLUSTER_COLORS[cls] ?? '#cad3df' : undefined,
          opacity: cls ? 0.95 : 0.3,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
        title={cls ?? ''}
      >
        {cls ? pretty(cls) : '—'}
      </span>
      <span style={{ fontSize: 11, minWidth: 0 }}>
        {difficulty ? (
          <DifficultyTag difficulty={difficulty} />
        ) : (
          <span style={{ opacity: 0.3 }}>—</span>
        )}
      </span>
      <span style={{ opacity: 0.7, fontSize: 11, textAlign: 'right' }}>{meta}</span>
    </div>
  );
}

function ExpandPanel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 14px 12px 36px',
        background: 'rgba(255,255,255,0.015)',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {children}
    </div>
  );
}

function ChipRack({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>{children}</div>
  );
}

/** Chip kinds that map to a filterable facet type (clicking filters). */
const KIND_TO_FACET: Partial<Record<string, FacetType>> = {
  family: 'family',
  concept: 'concept',
  tag: 'tag',
  category: 'category',
  submissionType: 'sub',
  gi: 'gi',
  difficulty: 'diff',
  zone: 'zone',
  posture: 'posture',
  dominance: 'dom',
};

function Chip({
  kind,
  label,
}: {
  kind:
    | 'positionType'
    | 'zone'
    | 'dominance'
    | 'posture'
    | 'family'
    | 'category'
    | 'submissionType'
    | 'gi'
    | 'difficulty'
    | 'concept'
    | 'tag'
    | 'canonical'
    | 'probability';
  label: string;
}) {
  const addListFilterTag = useAppStore((s) => s.addListFilterTag);
  const facetType = KIND_TO_FACET[kind];
  let color = '#cad3df';
  let prefix: string | null = null;
  if (kind === 'positionType') {
    color = CLUSTER_COLORS[label] ?? '#cad3df';
  } else if (kind === 'family' || kind === 'concept') {
    color = clusterColorForId(label, kind);
    if (kind === 'family') prefix = 'family';
    if (kind === 'concept') prefix = 'concept';
  } else if (kind === 'zone') {
    color = ZONE_COLORS[label as Zone] ?? '#888';
    prefix = 'zone';
  } else if (kind === 'category') {
    prefix = 'category';
  } else if (kind === 'gi') {
    prefix = 'gi';
  } else if (kind === 'submissionType') {
    prefix = 'sub';
  } else if (kind === 'dominance') {
    prefix = 'dom';
  } else if (kind === 'posture') {
    prefix = 'posture';
  } else if (kind === 'difficulty') {
    prefix = 'diff';
  } else if (kind === 'tag') {
    prefix = '#';
  } else if (kind === 'probability') {
    prefix = null;
  }

  return (
    <span
      role={facetType ? 'button' : undefined}
      tabIndex={facetType ? 0 : undefined}
      onClick={
        facetType
          ? (e) => {
              e.stopPropagation();
              addListFilterTag(makeFacet(facetType, label));
            }
          : undefined
      }
      title={facetType ? 'Filter the list by this tag' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 8,
        fontSize: 10,
        letterSpacing: 0.3,
        color,
        background: `${color}14`,
        border: `1px solid ${color}44`,
        cursor: facetType ? 'pointer' : 'default',
      }}
    >
      {prefix && (
        <span style={{ opacity: 0.6, textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.5 }}>
          {prefix}
        </span>
      )}
      <span>{pretty(label)}</span>
    </span>
  );
}

/**
 * Color-tinted difficulty chip used in the diff column. Same palette as
 * MoveTable's row chip and Drawer's DifficultyBadge so the surfaces
 * line up visually — fundamental/intermediate/advanced/specialist each
 * have a stable colour identity.
 */
function DifficultyTag({ difficulty }: { difficulty: string }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    fundamental: { bg: 'rgba(118, 224, 174, 0.18)', fg: '#9cf2c4' },
    intermediate: { bg: 'rgba(155, 231, 255, 0.18)', fg: '#bfe9ff' },
    advanced: { bg: 'rgba(255, 187, 102, 0.18)', fg: '#ffd29a' },
    specialist: { bg: 'rgba(255, 107, 145, 0.18)', fg: '#ffaabc' },
  };
  const c = palette[difficulty] ?? { bg: 'rgba(255,255,255,0.06)', fg: '#cad3df' };
  return (
    <span
      style={{
        display: 'inline-block',
        background: c.bg,
        color: c.fg,
        padding: '2px 7px',
        borderRadius: 8,
        fontSize: 9,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {difficulty}
    </span>
  );
}

function SmallLine({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 4 }}>{children}</div>;
}

function ViewDetailsLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        background: 'transparent',
        color: '#9be7ff',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: 11,
        marginBottom: 8,
        textDecoration: 'underline',
      }}
    >
      view details →
    </button>
  );
}

function Link({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        color: '#9be7ff',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        cursor: 'pointer',
      }}
    >
      {children}
    </span>
  );
}

function PositionPair({
  from,
  to,
  fromId,
  toId,
  onSelect,
}: {
  from: Position | undefined;
  to: Position | undefined;
  fromId: string;
  toId: string;
  onSelect: (id: string) => void;
}) {
  // The transition ("where does this take me?") is the headline fact for
  // a move/counter, so it leads the expansion at a readable size rather
  // than trailing the description as fine print.
  return (
    <div
      style={{
        fontSize: 13.5,
        lineHeight: 1.45,
        color: '#cad3df',
        // Own segment: breathing room before the chips / description.
        margin: '0 0 14px',
      }}
    >
      <span style={{ opacity: 0.5 }}>from </span>
      <Link onClick={() => onSelect(fromId)}>{from?.name ?? fromId}</Link>
      <span style={{ opacity: 0.5 }}>{' → '}</span>
      <Link onClick={() => onSelect(toId)}>{to?.name ?? toId}</Link>
    </div>
  );
}

function InnerCounterList({
  title,
  counters,
  positionsById,
  movesById,
  onSelect,
  onViewDetails,
}: {
  title: string;
  counters: Counter[];
  positionsById: Map<string, Position>;
  movesById: Map<string, Move>;
  onSelect: (id: string, intercepts: string) => void;
  onViewDetails: (id: string, intercepts: string) => void;
}) {
  void movesById; // reserved for future cross-link to parent move
  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          opacity: 0.5,
          marginBottom: 6,
        }}
      >
        {title} ({counters.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {counters.map((c) => {
          const r = positionsById.get(c.result);
          return (
            <div
              key={c.id}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(c.id, c.intercepts);
              }}
              style={{
                fontSize: 11,
                padding: '4px 6px',
                borderRadius: 3,
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.025)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(155,231,255,0.10)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.025)';
              }}
            >
              <span>{c.name}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ opacity: 0.55, fontSize: 10 }}>
                  → {r?.name ?? c.result}
                </span>
                <span
                  className="bjjmap-list-view-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(c.id, c.intercepts);
                  }}
                  style={{
                    color: '#9be7ff',
                    fontSize: 10,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  view
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
